/* eslint-disable default-case */
/**
 * Notice: Some code was adapted from Material-UI's text field.
 *         Copyright (c) 2014 Call-Em-All (https://github.com/callemall/material-ui)
 */
import React from "react"
import ReactDOM from "react-dom"
import PropTypes from "prop-types"
import { KeyboardTimePicker } from "@material-ui/pickers"
import InputLabel from "@material-ui/core/InputLabel"
import Chip from "@material-ui/core/Chip"
import withStyles from "@material-ui/core/styles/withStyles"
import blue from "@material-ui/core/colors/blue"
import FormControl from "@material-ui/core/FormControl"
import FormHelperText from "@material-ui/core/FormHelperText"
import IconClock from "@material-ui/icons/WatchLater"
import { createMuiTheme } from "@material-ui/core"
import { ThemeProvider } from "@material-ui/styles"
import lightBlue from "@material-ui/core/colors/lightBlue"

//watch_later
import cx from "classnames"
import moment from "moment"

const defaultMaterialTheme = createMuiTheme({
  palette: {
    primary: lightBlue,
  },
  overrides: {
    MuiInputBase: {
      root: {
        width: 0,
        marginBottom: 2,
      },
    },
    MuiIconButton: {
      root: {
        padding: 6,
      },
    },
    MuiInputAdornment: {
      positionEnd: {
        margin: 0,
        marginLeft: 0,
      },
    },
  },
})

const styles = theme => {
  const light = theme.palette.type === "light"
  const bottomLineColor = light
    ? "rgba(0, 0, 0, 0.42)"
    : "rgba(255, 255, 255, 0.7)"

  return {
    root: {},
    inputRoot: {
      display: "inline-flex",
      flexWrap: "wrap",
      flex: 1,
      marginTop: 0,
      minWidth: 70,
      "&$outlined,&$filled": {
        boxSizing: "border-box",
      },
      "&$outlined": {
        paddingTop: 14,
      },
      "&$filled": {
        paddingTop: 28,
      },
    },
    input: {
      display: "inline-block",
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
      appearance: "none", // Remove border in Safari, doesn't seem to break anything in other browsers
      WebkitTapHighlightColor: "rgba(0,0,0,0)", // Remove mobile color flashing (deprecated style).
      float: "left",
      flex: 1,
    },
    chipContainer: {
      display: "flex",
      flexFlow: "row wrap",
      cursor: "text",
      marginBottom: -2,
      minHeight: 40,
      "&$labeled&$standard": {
        marginTop: 18,
      },
    },
    outlined: {
      "& input": {
        height: 16,
        paddingTop: 4,
        paddingBottom: 12,
        marginTop: 4,
        marginBottom: 4,
      },
    },
    standard: {},
    filled: {
      "& input": {
        height: 22,
        marginBottom: 4,
        marginTop: 4,
        paddingTop: 0,
      },
      "$marginDense & input": {
        height: 26,
      },
    },
    labeled: {},
    label: {
      top: 4,
      "&$outlined&:not($labelShrink)": {
        top: 2,
        "$marginDense &": {
          top: 5,
        },
      },
      "&$filled&:not($labelShrink)": {
        top: 15,
        "$marginDense &": {
          top: 20,
        },
      },
    },
    labelShrink: {
      top: 0,
    },
    helperText: {
      marginBottom: -20,
    },
    focused: {},
    disabled: {},
    underline: {
      "&:after": {
        borderBottom: `2px solid ${
          theme.palette.primary[light ? "dark" : "light"]
        }`,
        left: 0,
        bottom: 0,
        // Doing the other way around crash on IE 11 "''" https://github.com/cssinjs/jss/issues/242
        content: '""',
        position: "absolute",
        right: 0,
        transform: "scaleX(0)",
        transition: theme.transitions.create("transform", {
          duration: theme.transitions.duration.shorter,
          easing: theme.transitions.easing.easeOut,
        }),
        pointerEvents: "none", // Transparent to the hover style.
      },
      "&$focused:after": {
        transform: "scaleX(1)",
      },
      "&$error:after": {
        borderBottomColor: theme.palette.error.main,
        transform: "scaleX(1)", // error is always underlined in red
      },
      "&:before": {
        borderBottom: `1px solid ${bottomLineColor}`,
        left: 0,
        bottom: 0,
        // Doing the other way around crash on IE 11 "''" https://github.com/cssinjs/jss/issues/242
        content: '"\\00a0"',
        position: "absolute",
        right: 0,
        transition: theme.transitions.create("border-bottom-color", {
          duration: theme.transitions.duration.shorter,
        }),
        pointerEvents: "none", // Transparent to the hover style.
      },
      "&:hover:not($disabled):not($focused):not($error):before": {
        borderBottom: `2px solid ${theme.palette.text.primary}`,
        // Reset on touch devices, it doesn't add specificity
        "@media (hover: none)": {
          borderBottom: `1px solid ${bottomLineColor}`,
        },
      },
      "&$disabled:before": {
        borderBottomStyle: "dotted",
      },
    },
    error: {
      "&:after": {
        backgroundColor: theme.palette.error.main,
        transform: "scaleX(1)", // error is always underlined in red
      },
    },
    chip: {
      margin: "0 8px 8px 0",
      float: "left",
    },
    marginDense: {},
  }
}

// const keyCodes = {
//   BACKSPACE: 8,
//   DELETE: 46,
//   LEFT_ARROW: 37,
//   RIGHT_ARROW: 39,
// }

class ChipTimeInput extends React.Component {
  state = {
    chips: [],
    errorText: undefined,
    focusedChip: null,
    inputValue: new Date(),
    isClean: true,
    isFocused: false,
    chipsUpdated: false,
    prevPropsValue: [],
  }

  constructor(props) {
    super(props)
    if (props.defaultValue) {
      this.state.chips = props.defaultValue
    }
    this.labelRef = React.createRef()
    this.input = React.createRef()
  }

  componentDidMount() {
    if (this.props.variant === "outlined") {
      this.labelNode = ReactDOM.findDOMNode(this.labelRef.current)
      this.forceUpdate()
    }
  }

  componentWillUnmount() {
    clearTimeout(this.inputBlurTimeout)
  }

  static getDerivedStateFromProps(props, state) {
    let newState = null

    if (props.value && props.value.length !== state.prevPropsValue.length) {
      newState = { prevPropsValue: props.value }
      if (props.clearInputValueOnChange) {
        newState.inputValue = new Date()
      }
    }

    // if change detection is only needed for clearInputValueOnChange
    if (
      props.clearInputValueOnChange &&
      props.value &&
      props.value.length !== state.prevPropsValue.length
    ) {
      newState = { prevPropsValue: props.value, inputValue: new Date() }
    }

    if (props.disabled) {
      newState = { ...newState, focusedChip: null }
    }

    if (!state.chipsUpdated && props.defaultValue) {
      newState = { ...newState, chips: props.defaultValue }
    }

    return newState
  }

  /**
   * Blurs this component.
   * @public
   */
  blur() {
    if (this.input) this.actualInput.blur()
  }

  /**
   * Focuses this component.
   * @public
   */
  focus = () => {
    // this.actualInput.focus()
    // if (this.state.focusedChip != null) {
    //   this.setState({ focusedChip: null })
    // }
  }

  handleUpdateInput = e => {
    const newValue = moment(e).format("hh:mm a")
    const numChipsBefore = (this.props.value || this.state.chips).length
    setTimeout(() => {
      const numChipsAfter = (this.props.value || this.state.chips).length
      if (numChipsBefore === numChipsAfter) {
        this.handleAddChip(newValue)
      } else {
        this.clearInput()
      }
    }, 150)
  }

  /**
   * Handles adding a chip.
   * @param {string|object} chip Value of the chip, either a string or an object (if dataSourceConfig is set)
   * @returns True if the chip was added (or at least `onAdd` was called), false if adding the chip was prevented
   */
  handleAddChip(chip) {
    if (this.props.onBeforeAdd && !this.props.onBeforeAdd(chip)) {
      this._preventChipCreation = true
      return false
    }
    this.clearInput()
    const chips = this.props.value || this.state.chips

    if (this.props.dataSourceConfig) {
      if (typeof chip === "string") {
        chip = {
          [this.props.dataSourceConfig.text]: chip,
          [this.props.dataSourceConfig.value]: chip,
        }
      }

      if (
        this.props.allowDuplicates ||
        !chips.some(
          c =>
            c[this.props.dataSourceConfig.value] ===
            chip[this.props.dataSourceConfig.value]
        )
      ) {
        if (this.props.value && this.props.onAdd) {
          this.props.onAdd(chip)
        } else {
          this.updateChips([...this.state.chips, chip])
        }
      }
      return true
    }

    if (chip.trim().length > 0) {
      if (this.props.allowDuplicates || chips.indexOf(chip) === -1) {
        if (this.props.value && this.props.onAdd) {
          this.props.onAdd(chip)
        } else {
          this.updateChips([...this.state.chips, chip])
        }
      }
      return true
    }
    return false
  }

  handleDeleteChip(chip, i) {
    if (!this.props.value) {
      const chips = this.state.chips.slice()
      const changed = chips.splice(i, 1) // remove the chip at index i
      if (changed) {
        let focusedChip = this.state.focusedChip
        if (this.state.focusedChip === i) {
          focusedChip = null
        } else if (this.state.focusedChip > i) {
          focusedChip = this.state.focusedChip - 1
        }
        this.updateChips(chips, { focusedChip })
      }
    } else if (this.props.onDelete) {
      this.props.onDelete(chip, i)
    }
  }

  updateChips(chips, additionalUpdates = {}) {
    this.setState({ chips, chipsUpdated: true, ...additionalUpdates })
    if (this.props.onChange) {
      this.props.onChange(chips)
    }
  }

  /**
   * Clears the text field for adding new chips.
   * This only works in uncontrolled input mode, i.e. if the inputValue prop is not used.
   * @public
   */
  clearInput() {
    this.updateInput("")
  }

  updateInput(value) {
    this.setState({ inputValue: value })
  }

  /**
   * Set the reference to the actual input, that is the input of the Input.
   * @param {object} ref - The reference
   */
  setActualInputRef = ref => {
    this.actualInput = ref
    if (this.props.inputRef) {
      this.props.inputRef(ref)
    }
  }

  render() {
    const {
      allowDuplicates,
      alwaysShowPlaceholder,
      blurBehavior,
      children,
      chipRenderer = defaultChipRenderer,
      classes,
      className,
      clearInputValueOnChange,
      dataSource,
      dataSourceConfig,
      defaultValue,
      delayBeforeAdd,
      disabled,
      disableUnderline,
      error,
      filter,
      FormHelperTextProps,
      fullWidth,
      fullWidthInput,
      helperText,
      id,
      InputProps,
      inputRef,
      InputLabelProps = {},
      inputValue,
      label,
      newChipKeyCodes,
      newChipKeys,
      onBeforeAdd,
      onAdd,
      onBlur,
      onDelete,
      onChange,
      onFocus,
      onKeyDown,
      onKeyPress,
      onKeyUp,
      onUpdateInput,
      placeholder,
      readOnly,
      required,
      rootRef,
      value,
      variant,
      basePath,
      ...other
    } = this.props

    const chips = value || this.state.chips
    const actualInputValue =
      inputValue != null ? inputValue : this.state.inputValue

    const hasInput =
      (this.props.value || actualInputValue).length > 0 ||
      actualInputValue.length > 0
    const shrinkFloatingLabel =
      InputLabelProps.shrink != null
        ? InputLabelProps.shrink
        : label != null &&
          (hasInput || this.state.isFocused || chips.length > 0)

    const chipComponents = chips.map((chip, i) => {
      const value = dataSourceConfig ? chip[dataSourceConfig.value] : chip
      return chipRenderer(
        {
          value,
          text: dataSourceConfig ? chip[dataSourceConfig.text] : chip,
          chip,
          isDisabled: !!disabled,
          isReadOnly: readOnly,
          isFocused: this.state.focusedChip === i,
          handleClick: () => this.setState({ focusedChip: i }),
          handleDelete: () => this.handleDeleteChip(chip, i),
          className: classes.chip,
        },
        i
      )
    })

    return (
      <FormControl
        ref={rootRef}
        fullWidth={fullWidth}
        className={cx(className, classes.root, {
          [classes.marginDense]: other.margin === "dense",
        })}
        error={error}
        required={required}
        onClick={this.focus}
        disabled={disabled}
        variant={variant}
        {...other}
      >
        {label && (
          <InputLabel
            htmlFor={id}
            classes={{
              root: cx(classes[variant], classes.label),
              shrink: classes.labelShrink,
            }}
            shrink={shrinkFloatingLabel}
            focused={this.state.isFocused}
            variant={variant}
            ref={this.labelRef}
            {...InputLabelProps}
          >
            {label}
          </InputLabel>
        )}
        <div
          className={cx(classes[variant], classes.chipContainer, {
            [classes.focused]: this.state.isFocused,
            [classes.underline]: !disableUnderline && variant === "standard",
            [classes.disabled]: disabled,
            [classes.labeled]: label != null,
            [classes.error]: error,
          })}
        >
          {variant === "standard" && chipComponents}
          <ThemeProvider theme={defaultMaterialTheme}>
            <KeyboardTimePicker
              label=""
              id={id}
              value={null}
              variant={"dialog"}
              keyboardIcon={<IconClock />}
              onChange={this.handleUpdateInput}
            />
          </ThemeProvider>
        </div>
        {helperText && (
          <FormHelperText
            {...FormHelperTextProps}
            className={
              FormHelperTextProps
                ? cx(FormHelperTextProps.className, classes.helperText)
                : classes.helperText
            }
          >
            {helperText}
          </FormHelperText>
        )}
      </FormControl>
    )
  }
}

ChipTimeInput.propTypes = {
  /** Allows duplicate chips if set to true. */
  allowDuplicates: PropTypes.bool,
  /** If true, the placeholder will always be visible. */
  alwaysShowPlaceholder: PropTypes.bool,
  /** Behavior when the chip input is blurred: `'clear'` clears the input, `'add'` creates a chip and `'ignore'` keeps the input. */
  blurBehavior: PropTypes.oneOf(["clear", "add", "ignore"]),
  /** A function of the type `({ value, text, chip, isFocused, isDisabled, isReadOnly, handleClick, handleDelete, className }, key) => node` that returns a chip based on the given properties. This can be used to customize chip styles.  Each item in the `dataSource` array will be passed to `chipRenderer` as arguments `chip`, `value` and `text`. If `dataSource` is an array of objects and `dataSourceConfig` is present, then `value` and `text` will instead correspond to the object values defined in `dataSourceConfig`. If `dataSourceConfig` is not set and `dataSource` is an array of objects, then a custom `chipRenderer` must be set. `chip` is always the raw value from `dataSource`, either an object or a string. */
  chipRenderer: PropTypes.func,
  /** Whether the input value should be cleared if the `value` prop is changed. */
  clearInputValueOnChange: PropTypes.bool,
  /** Data source for auto complete. This should be an array of strings or objects. */
  dataSource: PropTypes.array,
  /** Config for objects list dataSource, e.g. `{ text: 'text', value: 'value' }`. If not specified, the `dataSource` must be a flat array of strings or a custom `chipRenderer` must be set to handle the objects. */
  dataSourceConfig: PropTypes.shape({
    text: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }),
  /** The chips to display by default (for uncontrolled mode). */
  defaultValue: PropTypes.array,
  /** Whether to use `setTimeout` to delay adding chips in case other input events like `onSelection` need to fire first */
  delayBeforeAdd: PropTypes.bool,
  /** Disables the chip input if set to true. */
  disabled: PropTypes.bool,
  /** Disable the input underline. Only valid for 'standard' variant */
  disableUnderline: PropTypes.bool,
  /** Props to pass through to the `FormHelperText` component. */
  FormHelperTextProps: PropTypes.object,
  /** If true, the chip input will fill the available width. */
  fullWidth: PropTypes.bool,
  /** If true, the input field will always be below the chips and fill the available space. By default, it will try to be beside the chips. */
  fullWidthInput: PropTypes.bool,
  /** Helper text that is displayed below the input. */
  helperText: PropTypes.node,
  /** Props to pass through to the `InputLabel`. */
  InputLabelProps: PropTypes.object,
  /** Props to pass through to the `Input`. */
  InputProps: PropTypes.object,
  /** Use this property to pass a ref callback to the native input component. */
  inputRef: PropTypes.func,
  /** The input value (enables controlled mode for the text input if set). */
  inputValue: PropTypes.string,
  /* The content of the floating label. */
  label: PropTypes.node,
  /** The key codes (`KeyboardEvent.keyCode`) used to determine when to create a new chip. */
  newChipKeyCodes: PropTypes.arrayOf(PropTypes.number),
  /** The keys (`KeyboardEvent.key`) used to determine when to create a new chip. */
  newChipKeys: PropTypes.arrayOf(PropTypes.string),
  /** Callback function that is called when a new chip was added (in controlled mode). */
  onAdd: PropTypes.func,
  /** Callback function that is called with the chip to be added and should return true to add the chip or false to prevent the chip from being added without clearing the text input. */
  onBeforeAdd: PropTypes.func,
  /** Callback function that is called when the chips change (in uncontrolled mode). */
  onChange: PropTypes.func,
  /** Callback function that is called when a new chip was removed (in controlled mode). */
  onDelete: PropTypes.func,
  /** Callback function that is called when the input changes. */
  onUpdateInput: PropTypes.func,
  /** A placeholder that is displayed if the input has no values. */
  placeholder: PropTypes.string,
  /** Makes the chip input read-only if set to true. */
  readOnly: PropTypes.bool,
  /** The chips to display (enables controlled mode if set). */
  value: PropTypes.array,
  /** The variant of the Input component */
  variant: PropTypes.oneOf(["outlined", "standard", "filled"]),
}

ChipTimeInput.defaultProps = {
  allowDuplicates: false,
  blurBehavior: "clear",
  clearInputValueOnChange: false,
  delayBeforeAdd: false,
  disableUnderline: false,
  newChipKeyCodes: [13],
  newChipKeys: ["Enter"],
  variant: "standard",
}

export default withStyles(styles, { name: "WAMuiChipInput" })(ChipTimeInput)

export const defaultChipRenderer = (
  {
    text,
    isFocused,
    isDisabled,
    isReadOnly,
    handleClick,
    handleDelete,
    className,
  },
  key
) => (
  <Chip
    key={key}
    className={className}
    style={{
      pointerEvents: isDisabled || isReadOnly ? "none" : undefined,
      backgroundColor: isFocused ? blue[300] : undefined,
    }}
    onClick={handleClick}
    onDelete={handleDelete}
    label={text}
  />
)
